import React, { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LucideIcon,
    Home,
    Calendar,
    Menu,
    LayoutList,
    LogOut, BookCopy,
    ChevronDown, UsersRound,
    Settings,
    FileSpreadsheet,
    Activity,
    History
} from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';
import UserProfileImage from '../KanbanBoard/UserProfileImage';
import { styleConfig } from '../../constants/design';
import { useAppState } from '../../context/AppStateContext';
import KoMoldLogo from './KoMoldLogo';

export interface NavItem {
    icon: LucideIcon;
    label: string;
    to: string;
    children?: NavItem[];
    roles?: string[];
    showInMobile?: boolean;
}

interface NavSectionProps {
    title?: string;
    children: React.ReactNode;
}

const NavSection: React.FC<NavSectionProps> = ({ title, children }) => (
    <div className="space-y-2">
        {title && (
            <h3 className="px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                {title}
            </h3>
        )}
        <div className={title ? "space-y-1" : "space-y-2"}>
            {children}
        </div>
    </div>
);

interface SidebarNavItemProps extends NavItem {
    isExpanded: boolean;
    isChild?: boolean;
    depth?: number;
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
    icon: Icon,
    label,
    to,
    isExpanded,
    children,
    depth = 0,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const hasChildren = children && children.length > 0;

    const isActive = location.pathname === to ||
        (hasChildren && children.some(child => location.pathname === child.to));

    const handleClick = (e: React.MouseEvent) => {
        if (hasChildren && isExpanded) {
            e.preventDefault();
            setIsOpen(!isOpen);
        }
    };

    const paddingLeft = depth > 0 ? `pl-${4 + depth * 4}` : 'pl-3';

    return (
        <>
            <NavLink
                to={to}
                onClick={handleClick}
                className={({ isActive: navIsActive }) => `
          group relative flex items-center rounded-lg transition-all duration-200
          ${isExpanded ? `gap-3 px-3 py-2.5 ${paddingLeft}` : 'flex-col justify-center p-3'}
          text-sm font-medium
          ${navIsActive || isActive
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }
        `}
            >
                <Icon
                    className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                        }`}
                />

                {isExpanded && (
                    <>
                        <span className="flex-1 truncate">{label}</span>
                        {hasChildren && (
                            <ChevronDown
                                className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                                    }`}
                            />
                        )}
                    </>
                )}

                {/* Show label below icon when collapsed */}
                {!isExpanded && (
                    <span className="hidden xl:block text-xs text-center mt-1 truncate w-full leading-tight">
                        {label}
                    </span>
                )}

                {/* Tooltip for collapsed state on smaller screens */}
                {!isExpanded && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 xl:hidden">
                        {label}
                    </div>
                )}
            </NavLink>

            {/* Submenu */}
            {hasChildren && isOpen && isExpanded && (
                <div className="mt-1 space-y-1">
                    {children.map((child) => (
                        <SidebarNavItem
                            key={child.to}
                            {...child}
                            isExpanded={isExpanded}
                            isChild={true}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

interface ModernSidebarProps {
    isOpen?: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

const ModernSidebar: React.FC<ModernSidebarProps> = ({
    isOpen = false,
    isCollapsed = true,
    onToggleCollapse
}) => {
    const [internalCollapsed, setInternalCollapsed] = useState(false);
    const { user, logout } = useAuth();
    const { featureToggles, appName } = useAppState();

    const version = process.env.VITE_APP_VERSION;
    const commitHash = process.env.VITE_APP_COMMIT_HASH?.substring(0, 7);

    // Use external collapsed state if provided, otherwise use internal
    const isExpanded = isCollapsed !== undefined ? !isCollapsed : !internalCollapsed;

    const handleToggleCollapse = () => {
        if (onToggleCollapse) {
            onToggleCollapse();
        } else {
            setInternalCollapsed(!internalCollapsed);
        }
    };

    // Get navigation items based on feature toggles
    const getNavItems = (): NavItem[] => {
        return [
            { icon: Home, label: 'หน้าแรก', to: '/', showInMobile: true },
            {
                icon: Calendar,
                label: 'บอร์ดใบงาน',
                to: ROUTES.DASHBOARD,
                showInMobile: true,
                children: [
                    { icon: styleConfig.maintenance.icon, label: 'บอร์ดบำรุงรักษา', to: `${ROUTES.DASHBOARD}?boardType=maintenance` },
                    { icon: styleConfig.repair.icon, label: 'บอร์ดใบซ่อม', to: `${ROUTES.DASHBOARD}?boardType=repair` },
                    { icon: styleConfig.newMold.icon as LucideIcon, label: 'ใบรับแม่พิมพ์', to: `${ROUTES.DASHBOARD}?boardType=mold_acceptance` },
                ]
            },
            { icon: LayoutList, label: 'ทะเบียนแม่พิมพ์', to: ROUTES.MOLDS },
            { icon: BookCopy, label: 'ทะเบียนใบงาน', to: ROUTES.MOLD_ORDERS },
            { icon: History, label: 'ประวัติสถานะใบงาน', to: ROUTES.STATE_ACTIVITIES },
            { icon: UsersRound, label: 'ทะเบียนผู้ใช้งาน', to: ROUTES.USERS, roles: ['ADMIN'] },
            { icon: FileSpreadsheet, label: 'ส่งออกใบงาน (CSV)', to: ROUTES.ORDER_EXPORT, roles: ['ADMIN'] },
            { icon: Settings, label: 'ตั้งค่า', to: ROUTES.CONFIGURATION, roles: ['ADMIN'], showInMobile: true },
            { icon: Activity, label: 'สถานะระบบ', to: ROUTES.SYSTEM_STATUS, roles: ['ADMIN'] },
        ];
    };

    const navItems = useMemo(() => {
        return getNavItems().filter((item: NavItem) =>
            !item.roles || (user?.role && item.roles.includes(user.role))
        );
    }, [user?.role]);

    const handleLogout = async () => {
        try {
            if (window.confirm('คุณต้องการออกจากระบบใช่หรือไม่?')) {
                await logout();
            }
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };
    const displayAppName = appName.toUpperCase();

    return (
        <aside
            className={`
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        fixed lg:static inset-y-0 lg:inset-y-auto lg:h-full left-0 z-50 
        ${isExpanded ? 'w-72' : 'w-21'} 
        bg-white border-r border-gray-200 
        transition-all duration-300 ease-in-out
        flex flex-col
      `}
        >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 relative">
                {isExpanded ? (
                    <>
                        <div className="flex items-center gap-3">
                            <KoMoldLogo size={32} />
                            <span className="font-semibold text-gray-900">KoMold APPS</span>
                        </div>
                        <button
                            onClick={handleToggleCollapse}
                            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                            title="Collapse sidebar"
                        >
                            <Menu className="h-4 w-4 text-gray-500" />
                        </button>
                    </>
                ) : (
                    <button
                        onClick={handleToggleCollapse}
                        className={`
                w-full flex justify-center rounded-md items-center gap-0
                hover:bg-gray-100 transition-colors
                ${isExpanded ? 'p-2' : 'p-0'}`}
                        title="Expand sidebar"
                    >
                        <KoMoldLogo size={32} className="flex-shrink-0" transparent={true} />
                        <span className="text-base font-semibold text-blue-800 leading-tight pt-0.5">
                            KoMold
                        </span>
                    </button>
                )}
            </div>

            {/* User Profile */}
            {isExpanded && user && (
                <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <UserProfileImage
                            currentAssignee={{
                                id: user.user_id,
                                username: user.full_name,
                                isActive: true,
                                roleId: 0,
                                createdAt: '',
                                updatedAt: '',
                                name: user.full_name
                            }}
                            size="md"
                        />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                                {user.full_name}
                            </p>
                            <p className="text-xs text-gray-500 capitalize">
                                {user.role.toLowerCase()}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-6 overflow-y-auto">
                {/* Main Section */}
                <NavSection title={isExpanded ? "หลัก" : undefined}>
                    {navItems.map((item) => (
                        <SidebarNavItem key={item.to} {...item} isExpanded={isExpanded} />
                    ))}
                </NavSection>
            </nav>

            {/* Footer */}
            <div className="border-t border-gray-200 p-3 space-y-3">
                {/* Logout Button */}
                <button
                    onClick={handleLogout}
                    className={`
            w-full flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 
            rounded-lg hover:bg-red-50 hover:text-red-700 transition-all duration-200
            ${!isExpanded ? 'justify-center' : ''}
          `}
                >
                    <LogOut className="h-5 w-5 flex-shrink-0" />
                    {isExpanded && <span>ออกจากระบบ</span>}
                </button>

                {/* Version Info */}
                {isExpanded && (version || commitHash) && (
                    <div className="text-center text-xs text-gray-400 space-y-1">
                        {version && <div>v{version}</div>}
                        {commitHash && <div>{commitHash}</div>}
                    </div>
                )}
            </div>
        </aside>
    );
};

export default ModernSidebar;
